import { useEffect, useRef } from 'react';
import { useSelector, useDispatch, shallowEqual } from 'react-redux';
import { hydrateFive9 } from 'src/actions/five9Actions';
import { selectPersistableFive9, PERSIST_SCHEMA_VERSION } from 'src/reducers/five9Reducer';

/**
 * @typedef {import('src/type-defs/five9Types').Five9PersistedSnapshot} Five9PersistedSnapshot
 * @typedef {import('src/type-defs/five9Types').Five9CallSyncMessage} Five9CallSyncMessage
 */

const CHANNEL_NAME = 'five9-call-state';
const INITIAL_SYNC_WINDOW_MS = 500;
const ORIGIN_ID = `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/**
 * Keeps the persisted Five9 call slice synchronized across tabs.
 *
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {string} [options.scope='global']
 */
export default function useFive9CallStateSync(options = {}) {
  const { enabled = true, scope = 'global' } = options;
  const reduxDispatch = useDispatch();
  const persistable = useSelector(selectPersistableFive9, shallowEqual);
  const suppressBroadcastRef = useRef(false);
  const channelRef = useRef(null);
  const lastBroadcastSerializedRef = useRef(null);
  const readyToBroadcastRef = useRef(false);
  const latestPersistableRef = useRef(persistable);

  useEffect(() => {
    latestPersistableRef.current = persistable;
  }, [persistable]);

  useEffect(() => {
    readyToBroadcastRef.current = false;
    suppressBroadcastRef.current = false;
    lastBroadcastSerializedRef.current = null;

    if (!enabled || !scope) {
      channelRef.current = null;
      return undefined;
    }

    if (typeof BroadcastChannel === 'undefined') return undefined;

    const channel = new BroadcastChannel(CHANNEL_NAME);
    channelRef.current = channel;

    const handleMessage = (event) => {
      const msg = event?.data;
      if (!msg || msg.origin === ORIGIN_ID) return;
      if (msg.scope !== scope) return;

      if (msg.type === 'snapshot' && msg.payload?.v === PERSIST_SCHEMA_VERSION) {
        readyToBroadcastRef.current = true;
        suppressBroadcastRef.current = true;
        reduxDispatch(hydrateFive9(msg.payload));
        setTimeout(() => {
          suppressBroadcastRef.current = false;
        }, 0);
      } else if (msg.type === 'request') {
        try {
          channel.postMessage({
            type: 'snapshot',
            origin: ORIGIN_ID,
            scope,
            payload: latestPersistableRef.current
          });
        } catch (_) {
          // Swallow channel errors.
        }
      }
    };

    channel.addEventListener('message', handleMessage);

    try {
      channel.postMessage({ type: 'request', origin: ORIGIN_ID, scope });
    } catch (_) {
      // Swallow channel errors.
    }

    const openGateTimer = setTimeout(() => {
      readyToBroadcastRef.current = true;
    }, INITIAL_SYNC_WINDOW_MS);

    return () => {
      clearTimeout(openGateTimer);
      channel.removeEventListener('message', handleMessage);
      channel.close();
      channelRef.current = null;
    };
  }, [reduxDispatch, enabled, scope]);

  useEffect(() => {
    if (!enabled || !scope) return;
    if (!channelRef.current) return;
    if (suppressBroadcastRef.current) return;

    const hasContent = !!(persistable?.activeCallSid
      || (persistable?.calls && Object.keys(persistable.calls).length > 0));

    if (!hasContent && !readyToBroadcastRef.current) return;
    if (hasContent) readyToBroadcastRef.current = true;

    let serialized;
    try {
      serialized = JSON.stringify(persistable);
    } catch (_) {
      serialized = null;
    }

    if (serialized !== null && serialized === lastBroadcastSerializedRef.current) return;

    try {
      channelRef.current.postMessage({
        type: 'snapshot',
        origin: ORIGIN_ID,
        scope,
        payload: persistable
      });
      lastBroadcastSerializedRef.current = serialized;
    } catch (_) {
      // Swallow channel errors.
    }
  }, [persistable, enabled, scope]);
}
